import {Instance} from "../../store/slices/instancesSlice";

export const getInstanceActions = (
    instance: Instance,
    deletingInstances: Set<string>,
    makingPublic: string | null,
    makingPrivate: string | null,
    isLimitedTrial = false
) => {
    const isManual = instance.metadata?.tag === 'manual';
    const isPublic = instance?.metadata?.isPublic === 'true';
    const sshEnabled = instance?.metadata?.enableSSH === 'true' || instance?.metadata?.SSHEnabled === 'true';

    // What Manage DNS actually needs is a routable address to forward to: Nginx PM has no route to
    // a tenant fixed IP, so a proxy host built on one points nowhere. `isPublic` is set by the
    // make-public and DNS flows and is the usual way to have one, but a cluster HERO holds the
    // cluster's reach address without ever going through either, so it would be locked out of the
    // one thing the role exists for. Ask for the address itself, not the flag that usually implies it.
    const hasFloatingIp = Object.values(instance.addresses || {})
        .flat()
        .some((addr: any) => addr?.["OS-EXT-IPS:type"] === 'floating');
    const dnsReachable = isPublic || hasFloatingIp;

    const actions = [
        {
            label: 'Manage DNS',
            value: 'manage_dns',
            isDisabled: instance.status === 'BUILD' ||
                instance.state?.startsWith('deleting') ||
                !dnsReachable
        },
        {
            label: 'Make Public',
            value: 'make_public',
            isDisabled: isPublic ||
                instance.status !== 'ACTIVE' ||
                instance.state?.startsWith('deleting') ||
                makingPublic === instance.id
        },
        {
            label: 'Make Private',
            value: 'make_private',
            isDisabled: !isPublic ||
                instance.status !== 'ACTIVE' ||
                instance.state?.startsWith('deleting') ||
                makingPrivate === instance.id
        },
        {
            label: isLimitedTrial ? 'Create Snapshot (not on limited trial)' : 'Create Snapshot',
            value: 'create_snapshot',
            isDisabled: isLimitedTrial ||
                instance.status === 'BUILD' ||
                instance.state?.startsWith('deleting')
        },
        {
            label: 'Start Instance',
            value: 'start_instance',
            isDisabled: instance.status !== 'SHUTOFF' || !!instance.task && instance.task !== 'N/A'
        },
        {
            label: 'Shut Off Instance',
            value: 'stop_instance',
            isDisabled: instance.status !== 'ACTIVE' || !!instance.task && instance.task !== 'N/A'
        },
        {
            label: 'Soft Reboot Instance',
            value: 'soft_reboot_instance',
            isDisabled: instance.status !== 'ACTIVE' || !!instance.task && instance.task !== 'N/A'
        },
        {
            label: 'Hard Reboot Instance',
            value: 'hard_reboot_instance',
            isDisabled: (instance.status !== 'ACTIVE' && instance.status !== 'SHUTOFF') || !!instance.task && instance.task !== 'N/A'
        },
        {
            label: isLimitedTrial ? 'Resize Instance (not on limited trial)' : 'Resize Instance',
            value: 'resize_instance',
            isDisabled: isLimitedTrial ||
                (instance.status !== 'ACTIVE' && instance.status !== 'SHUTOFF') || (!!instance.task && instance.task !== 'N/A')
        },
        {
            label: 'Confirm Resize',
            value: 'confirm_resize',
            isDisabled: instance.status !== 'VERIFY_RESIZE' || !!instance.task && instance.task !== 'N/A'
        },
        {
            label: 'Revert Resize',
            value: 'revert_resize',
            isDisabled: instance.status !== 'VERIFY_RESIZE' || !!instance.task && instance.task !== 'N/A'
        },
        {
            label: isLimitedTrial ? 'Upgrade to Cluster (not on limited trial)' : 'Upgrade to Cluster',
            value: 'upgrade_to_cluster',
            isDisabled: isLimitedTrial ||
                instance.status !== 'ACTIVE' ||
                (!!instance.task && instance.task !== 'N/A')
        },
        {
            label: 'Change Security Group',
            value: 'change_security_group',
            isDisabled: instance.status === 'BUILD' ||
                instance.task?.startsWith('deleting')
        },
        {
            label: 'Copy SSH',
            value: 'copy_ssh',
            isDisabled: !sshEnabled ||
                instance.status === 'BUILD' ||
                instance.task?.startsWith('deleting')
        },
        {
            label: 'Delete Instance',
            value: 'delete_instance',
            isDisabled: instance.status === 'BUILD' ||
                instance.task?.startsWith('deleting') ||
                deletingInstances.has(instance.id)
        }
    ];

    // Cluster members are disposable, autoscaling-managed clones. You manage the CLUSTER (via the hero
    // VM + a rolling update), never an individual member, so only offer lifecycle actions and hide the
    // rest - Manage DNS / Make Public-Private / Snapshot / Resize / Upgrade / Change SG / Copy SSH /
    // Delete are all either impossible on a member or fought/re-healed by the autoscaling group.
    if ((instance as any).cluster_member) {
        const memberAllowed = ['start_instance', 'stop_instance', 'soft_reboot_instance', 'hard_reboot_instance'];
        // ...EXCEPT the hero, which is the one member you are supposed to reach and change. It holds
        // the cluster's floating IP precisely so there is a single origin for a mutation, so hiding
        // Copy SSH on it would remove the only route to the thing the role exists for. Managed
        // datastore members became cluster_member=true when the listing learned to read their
        // metadata; before that this branch never saw a hero at all.
        // ...and Manage DNS with it. The hero is the cluster's single stable origin - it keeps the
        // reach address across a promotion - so a name pointed at it survives the hero moving,
        // which is exactly what you want for an admin route to one member. The cluster's own public
        // URL is a separate thing and still belongs to the load balancer, not here.
        if ((instance as any).hero_of) memberAllowed.push('copy_ssh', 'manage_dns');
        return actions.filter(action => memberAllowed.includes(action.value));
    }

    if (isManual) {
        return actions.filter(action => action.value !== 'make_public' && action.value !== 'make_private');
    }

    const allowedActions = [
        'start_instance',
        'stop_instance',
        'soft_reboot_instance',
        'hard_reboot_instance',
        'make_public',
        'make_private',
        'create_snapshot',
        'copy_ssh',
        'manage_dns',
        'change_security_group',
        'resize_instance',
        'confirm_resize',
        'revert_resize',
        'upgrade_to_cluster',
        'delete_instance',
    ];
    return actions.filter(action => allowedActions.includes(action.value));
};